import React, { useMemo, useState } from 'react';
import FamilyDialog from './FamilyDialog';
import type { FamilyToolsMember } from './types';
import { callManageFamily } from './manageFamilyClient';
import { isJuniorForYear, clubYear } from '@shared/membership';

interface GroupIntoFamilyDialogProps {
  /** Candidate individual members (not already in a family), always including the member the admin started from. */
  startingMember: FamilyToolsMember;
  allMembers: FamilyToolsMember[];
  onClose: () => void;
  onDone: (result: { message: string; changedMemberIds: string[] }) => void;
}

const GroupIntoFamilyDialog: React.FC<GroupIntoFamilyDialogProps> = ({
  startingMember,
  allMembers,
  onClose,
  onDone,
}) => {
  const year = clubYear();
  const candidates = useMemo(
    () => allMembers.filter((m) => !m.familyGroupId),
    [allMembers]
  );

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set([startingMember.id]));
  const [primaryMemberId, setPrimaryMemberId] = useState<string | null>(
    isJuniorForYear(startingMember.dateOfBirth, year) ? null : startingMember.id
  );
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return candidates;
    return candidates.filter(
      (m) =>
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(term) ||
        (m.memberNumber || '').toLowerCase().includes(term)
    );
  }, [candidates, search]);

  const selectedMembers = candidates.filter((m) => selectedIds.has(m.id));
  const eligiblePrimaries = selectedMembers.filter((m) => !isJuniorForYear(m.dateOfBirth, year));
  const canSubmit = selectedIds.size >= 2 && !!primaryMemberId && selectedIds.has(primaryMemberId);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (primaryMemberId === id) setPrimaryMemberId(null);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit || !primaryMemberId) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await callManageFamily('GROUP', {
        memberIds: Array.from(selectedIds),
        primaryMemberId,
      });
      if (result.success) {
        onDone({
          message: result.message || 'The family was created.',
          changedMemberIds: result.changedMemberIds || [],
        });
      } else {
        setServerError(result.message || 'Could not group these members into a family. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not group these members into a family. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      titleId="group-into-family-title"
      title="Group into a family"
      descriptionId="group-into-family-desc"
      busy={submitting}
      onClose={onClose}
      widthClassName="max-w-lg"
    >
      <p id="group-into-family-desc" className="mt-1 text-sm text-gray-600">
        Select 2 or more individual members and choose a primary (an adult). Member numbers are
        kept and no payment is created.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4">
        <label htmlFor="gif-search" className="block text-sm font-medium text-gray-700 mb-1">
          Search members
        </label>
        <input
          id="gif-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name or member number"
          className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        />
      </div>

      <ul className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-gray-200">
        {matches.length === 0 ? (
          <li className="p-3 text-sm text-gray-500">No matching members found.</li>
        ) : (
          matches.map((m) => {
            const checked = selectedIds.has(m.id);
            return (
              <li key={m.id} className="flex min-h-[44px] items-center gap-2 border-b border-gray-100 px-3 py-2 last:border-b-0">
                <input
                  id={`gif-member-${m.id}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSelected(m.id)}
                  className="h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                />
                <label htmlFor={`gif-member-${m.id}`} className="flex-1 cursor-pointer text-sm text-gray-800">
                  {m.firstName} {m.lastName}
                  {m.memberNumber ? ` (${m.memberNumber})` : ''}
                </label>
              </li>
            );
          })
        )}
      </ul>

      <div className="mt-4">
        <label htmlFor="gif-primary" className="block text-sm font-medium text-gray-700 mb-1">
          Primary (must be an adult)
        </label>
        <select
          id="gif-primary"
          value={primaryMemberId || ''}
          onChange={(e) => setPrimaryMemberId(e.target.value || null)}
          disabled={eligiblePrimaries.length === 0}
          className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        >
          <option value="">Choose the primary</option>
          {eligiblePrimaries.map((m) => (
            <option key={m.id} value={m.id}>
              {m.firstName} {m.lastName}
            </option>
          ))}
        </select>
        {selectedMembers.length > 0 && eligiblePrimaries.length === 0 && (
          <p className="mt-1 text-sm text-amber-700">
            None of the selected members are adults, so none can be the primary.
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Grouping…' : 'Group into a family'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default GroupIntoFamilyDialog;
